import { FC } from 'react';
import {
  Check,
  Info,
  Printer,
  ShieldCheck,
  ShoppingCart,
  Sliders,
  Wallet,
} from 'lucide-react';

import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

import {
  SellPlan,
  SellSelection,
} from 'modules/incentivesMarket/pages/IncentiveOfferDetail/types/SellUnitsSelection';
import { useSellUnitsSelection } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/hooks/useSellUnitsSelection';

import IntentCard from './IntentCard';
import CustomUnitsInput from './CustomUnitsInput';
import AmountInput from './AmountInput';
import SellInvoiceTable from './SellInvoiceTable';

import './IncentiveOfferSellUnitsPanel.scss';

type IncentiveOfferSellUnitsPanelProps = {
  plans: SellPlan[];
  isDisabled: boolean;
  mustSellAll: boolean;
  minUnits: number;
  maxTotalUnits: number;
  onConfirm: (selection: SellSelection) => void;
  onViewOtherOffers: () => void;
};

const IncentiveOfferSellUnitsPanel: FC<IncentiveOfferSellUnitsPanelProps> = ({
  plans,
  isDisabled,
  mustSellAll,
  minUnits,
  maxTotalUnits,
  onConfirm,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();

  const { data, actions } = useSellUnitsSelection({
    plans,
    mustSellAll,
    minUnits,
    maxTotalUnits,
  });

  const {
    mode,
    customMode,
    selected,
    customUnits,
    amountInput,
    totals,
    totalMax,
    totalMaxNet,
    pct,
    hasUnits,
    isConfirmEnabled,
    selection,
  } = data;

  const controlsDisabled = isDisabled || mustSellAll;

  const handleConfirm = () => {
    if (!isConfirmEnabled || isDisabled) return;
    onConfirm(selection);
  };

  return (
    <div
      className="card card-bordered incentive-offer-sell-units-panel"
      style={{ position: 'sticky', top: '20px' }}
    >
      <header className="incentive-offer-sell-units-panel__header">
        <span className="incentive-offer-sell-units-panel__header-icon">
          <ShoppingCart size={22} strokeWidth={2.2} />
        </span>
        <div>
          <h5 className="mb-1">{t('IncentivesMarketSellUnits')}</h5>
          <p className="text-soft fs-13px mb-0">
            {t('IncentivesMarketSellUnitsSubtitle')}
          </p>
        </div>
      </header>

      <div className="row g-0">
        <section className="col-lg-6 incentive-offer-sell-units-panel__left">
          <div className="d-flex align-items-baseline justify-content-between mb-3">
            <h6 className="incentive-offer-sell-units-panel__section-title">
              {t('IncentivesMarketSellHowMuch')}
            </h6>
            <span className="incentive-offer-sell-units-panel__max-badge">
              <ShieldCheck size={13} />
              {t('IncentivesMarketSellMaxBadge', {
                units: formatNumber(totalMax),
              })}
            </span>
          </div>

          <div className="row">
            <div className="col-12 col-sm-4 d-flex">
              <IntentCard
                active={mode === 'max'}
                onClick={() => actions.pickMode('max')}
                label={t('IncentivesMarketSellIntentMax')}
                caption={t('IncentivesMarketSellIntentMaxCaption', {
                  units: formatNumber(totalMax),
                })}
                value={formatCurrencyDecimals(totalMaxNet)}
                accent
                disabled={controlsDisabled}
              />
            </div>
            <div className="col-12 col-sm-4 d-flex">
              <IntentCard
                active={mode === 'half'}
                onClick={() => actions.pickMode('half')}
                label={t('IncentivesMarketSellIntentHalf')}
                caption={t('IncentivesMarketSellIntentHalfCaption', {
                  units: formatNumber(Math.round(totalMax / 2)),
                })}
                value={formatCurrencyDecimals(totalMaxNet / 2)}
                disabled={controlsDisabled}
              />
            </div>
            <div className="col-12 col-sm-4 d-flex">
              <IntentCard
                active={mode === 'custom'}
                onClick={() => {
                  actions.pickMode('custom');
                  actions.setCustomMode('units');
                }}
                label={t('IncentivesMarketSellIntentCustom')}
                caption={t('IncentivesMarketSellIntentCustomCaption')}
                adjustLabel={t('IncentivesMarketSellIntentCustomAdjust')}
                icon={<Sliders size={18} />}
                disabled={controlsDisabled}
              />
            </div>
          </div>

          {mode === 'custom' && !mustSellAll && (
            <div className="incentive-offer-sell-units-panel__custom-panel">
              <div className="incentive-offer-sell-units-panel__toggle-group">
                <button
                  type="button"
                  className={`incentive-offer-sell-units-panel__toggle-btn${
                    customMode === 'units'
                      ? ' incentive-offer-sell-units-panel__toggle-btn--active'
                      : ''
                  }`}
                  onClick={() => actions.setCustomMode('units')}
                  disabled={controlsDisabled}
                >
                  {t('IncentivesMarketSellByUnits')}
                </button>
                <button
                  type="button"
                  className={`incentive-offer-sell-units-panel__toggle-btn${
                    customMode === 'amount'
                      ? ' incentive-offer-sell-units-panel__toggle-btn--active'
                      : ''
                  }`}
                  onClick={() => actions.setCustomMode('amount')}
                  disabled={controlsDisabled}
                >
                  {t('IncentivesMarketSellByAmount')}
                </button>
              </div>

              {customMode === 'units' ? (
                <CustomUnitsInput
                  value={customUnits}
                  max={maxTotalUnits}
                  netPreview={totals.net}
                  onChange={actions.setCustomTotalUnits}
                  disabled={controlsDisabled}
                />
              ) : (
                <AmountInput
                  value={amountInput}
                  max={totalMaxNet}
                  onChange={actions.applyAmount}
                  resultUnits={totals.units}
                  plans={plans}
                  selected={selected}
                  disabled={controlsDisabled}
                />
              )}
            </div>
          )}

          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center fs-12px mb-1">
              <span className="text-soft">
                {t('IncentivesMarketSellSelectedProgress', {
                  selected: formatNumber(totals.units),
                  total: formatNumber(totalMax),
                })}
              </span>
              <span className="fw-semibold text-secondary tnum">{pct}%</span>
            </div>
            <div className="incentive-offer-sell-units-panel__progress-bar">
              <div
                className="incentive-offer-sell-units-panel__progress-fill"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="d-flex align-items-center mt-2 mb-0 fs-12px text-soft">
              <Info size={12} />
              {t('IncentivesMarketSellSellerCapHint')}
            </p>
          </div>
        </section>

        <section className="col-lg-6 incentive-offer-sell-units-panel__right">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="incentive-offer-sell-units-panel__section-title">
              {t('IncentivesMarketSellYourSale')}
            </h6>
            <button
              type="button"
              className="btn btn-icon btn-trigger incentive-offer-sell-units-panel__print-btn"
              onClick={() => window.print()}
              disabled={!hasUnits}
              title={t('IncentivesMarketSellPrintSummary')}
            >
              <Printer size={16} />
            </button>
          </div>

          <SellInvoiceTable
            plans={plans}
            selected={selected}
            totals={totals}
            hasUnits={hasUnits}
          />

          <div className="incentive-offer-sell-units-panel__receive-panel">
            <div className="d-flex align-items-center ">
              <Wallet size={18} className="text-info" />
              <div className="ml-2">
                <p className="mb-0 fs-14px fw-medium text-light">
                  {t('IncentivesMarketSellReceive')}
                </p>
                <p className="mb-0 fs-11px text-white">
                  {t('IncentivesMarketSellReceiveHint')}
                </p>
              </div>
            </div>
            <p className="incentive-offer-sell-units-panel__receive-amount tnum mb-0">
              {formatCurrencyDecimals(totals.net)}
            </p>
          </div>

          {hasUnits && (
            <div className="incentive-offer-sell-units-panel__after-sale">
              <p className="mb-2 fs-12px fw-semibold text-uppercase text-soft">
                {t('IncentivesMarketSellAfterSale')}
              </p>
              <div className="tnum">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className="d-flex justify-content-between fs-14px text-secondary mb-1"
                  >
                    <span>{plan.name}</span>
                    <span className="fw-medium text-dark">
                      {formatNumber(plan.holdings - (selected[plan.id] || 0))}{' '}
                      {t('IncentivesMarketUnits').toLowerCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            disabled={!isConfirmEnabled || isDisabled}
            onClick={handleConfirm}
            className={`incentive-offer-sell-units-panel__cta${
              isConfirmEnabled && !isDisabled
                ? ' incentive-offer-sell-units-panel__cta--enabled'
                : ' incentive-offer-sell-units-panel__cta--disabled'
            }`}
            data-testid="sell-units-confirm-button"
          >
            <Check size={18} strokeWidth={2.5} />
            {hasUnits && isConfirmEnabled
              ? t('IncentivesMarketSellConfirmCta', {
                  units: formatNumber(totals.units),
                  amount: formatCurrencyDecimals(totals.net),
                })
              : t('IncentivesMarketSellSelectToContinue')}
          </button>

          <div className="text-center mt-3">
            <Button
              className="btn btn-link text-soft fs-13px"
              onClick={onViewOtherOffers}
              variant={ButtonVariants.LINK}
            >
              <em className="icon ni ni-arrow-left mr-1" />
              {t('ViewOtherOffers')}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default IncentiveOfferSellUnitsPanel;
